import bytes from 'bytes';
import classNames from 'classnames';
import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { FlexSpacer } from '@insights-gaming/material-components';
import { useCreateSelector } from '@insights-gaming/redux-utils';
import { createRemFromPx, Theme } from '@insights-gaming/theme';
import { secondsToHms } from '@insights-gaming/utils';
import { IUpload, LocalVideoLike, UploadingVideoState, VideoUploadState } from '@insights-gaming/video-upload-slice';
import { IconButton, Typography } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import PauseIcon from '@material-ui/icons/Pause';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';

import { createUploadQueueItem, UploadQueueItemProps, VideoMetadata } from '../createUploadQueueItem';

interface UploadQueueControlOwnProps {
  className?: string;
  abortUploadDialog: JSX.Element;
  openAbortUploadDialog: VoidFunction;
  totalEtaText: string;
  etaText: string;
  uploadSpeedText: string;
  uploadFileText: string;
  completedText: string;
  redirectOnClick?: VoidFunction;
  handlePauseCurrentUpload?: VoidFunction;
  handleResumeUpload?: VoidFunction;
  width?: number;
  height?: number;

  currentUploadMetadata?: VideoMetadata;
  sumOfQueuedFilesizes?: number;
}

export type UploadQueueControlProps = UploadQueueControlOwnProps;

const useStyles = makeStyles((theme: Theme) => createStyles({
  root: {
    position: 'absolute',
    backgroundColor: theme.palette.background.paper,
    width: ({ width = 460 }: UploadQueueControlProps) => createRemFromPx(width),
    height: ({ height = 250 }: UploadQueueControlProps) => createRemFromPx(height),
    overflow: 'hidden',
    borderRadius: theme.shape.borderRadius,
    zIndex: 5,
    '&$collapsed': {
      height: createRemFromPx(64),
    },
  },
  header: {
    backgroundColor: theme.palette.background.container,
    padding: theme.spacing(1, 1, 1, 3),
  },
  queueList: {
    overflowY: 'scroll',
    height: createRemFromPx(186),
  },
  static: {
    flexShrink: 0,
  },
  info: {
    overflow: 'hidden',
  },
  collapsed: {},
}), {name: 'UploadQueueControl'});

export function createUploadQueueControl<LV extends LocalVideoLike>(
  getUploadState: (state: any) => VideoUploadState<LV>,
  getCurrentUpload: (state: any) => UploadingVideoState<LV> | undefined,
  makeGetStartedUploadByVideoId: () => (state: any, props: { id: string | undefined }) => IUpload | undefined,
  uploadQueueControlHOC?: (Component: React.ComponentType<UploadQueueControlProps>) => React.ComponentType<UploadQueueControlProps>,
  uploadQueueItemHOC?: (Component: React.ComponentType<UploadQueueItemProps>) => React.ComponentType<UploadQueueItemProps>,
): React.FC<UploadQueueControlProps> & {
  UploadQueueControl: React.ComponentType<UploadQueueControlProps>;
  UploadQueueItem: React.ComponentType<UploadQueueItemProps>;
} {
  const _UploadQueueItem = createUploadQueueItem(getCurrentUpload);
  const UploadQueueItem = uploadQueueItemHOC ? uploadQueueItemHOC(_UploadQueueItem) : _UploadQueueItem;

  function _UploadQueueControl(props: UploadQueueControlProps) {
    const classes = useStyles(props);
    const {
      className,
      abortUploadDialog,
      openAbortUploadDialog,
      totalEtaText,
      etaText,
      uploadSpeedText,
      uploadFileText,
      redirectOnClick,
      handlePauseCurrentUpload,
      handleResumeUpload,
      completedText,
      currentUploadMetadata,
      sumOfQueuedFilesizes = 0,
    } = props;
    const { currentUpload, uploadQueue, uploadedVideosTemp, startedUploads } = useSelector(getUploadState);

    const [expanded, setExpanded] = useState(true);

    const toggleExpandedView = useCallback(() => setExpanded((prevState) => !prevState), []);

    const startedUpload = useCreateSelector(makeGetStartedUploadByVideoId, { id: currentUpload?.resumeInfo?.videoId });

    const totalEta = useMemo(
      () => {
        if (!(currentUploadMetadata && startedUpload && startedUpload.rate > 0)) {
          return undefined;
        }
        const currentUploadSize = currentUploadMetadata.size;
        if (!(currentUploadSize > 0)) {
          return undefined;
        }

        return secondsToHms(
          ((1 - startedUpload.progress) * currentUploadSize + sumOfQueuedFilesizes)
          / currentUploadSize
          / startedUpload.rate,
        );
      },
      [currentUploadMetadata, startedUpload, sumOfQueuedFilesizes],
    );

    const currentEta = useMemo(
      () => startedUpload && startedUpload.rate > 0
        ? secondsToHms((1 - startedUpload.progress) / startedUpload.rate)
        : undefined,
      [startedUpload],
    );

    const speed = useMemo(
      () => startedUpload && currentUploadMetadata && bytes(startedUpload.rate * currentUploadMetadata.size),
      [currentUploadMetadata, startedUpload],
    );

    return (
      <div className={classNames(classes.root, className, {[classes.collapsed]: !expanded})}>
        <FlexSpacer flexAlignItems='center' flexJustifyContent='space-between' className={classes.header}>
          <Typography variant='h3' className={classes.static}>
            {uploadFileText}
          </Typography>
          {totalEta && speed && (
            <FlexSpacer className={classes.info}>
              <Typography variant='caption'>
                {uploadSpeedText}
              </Typography>
              <Typography variant='caption'>|</Typography>
              <Typography align='right' variant='caption'>
                {totalEtaText}
              </Typography>
            </FlexSpacer>
          )}
          <div className={classes.static}>
            {handlePauseCurrentUpload ? (
              <IconButton size='small' onClick={handlePauseCurrentUpload}>
                <PauseIcon />
              </IconButton>
            ) : handleResumeUpload && (
              <IconButton size='small' onClick={handleResumeUpload}>
                <PlayArrowIcon />
              </IconButton>
            )}
            <IconButton onClick={toggleExpandedView}>
              {expanded ? <ExpandMoreIcon/> : <ExpandLessIcon/>}
            </IconButton>
            <IconButton onClick={openAbortUploadDialog}>
              <CloseIcon/>
            </IconButton>
          </div>
        </FlexSpacer>
        {expanded && (
          <div className={classes.queueList}>
            {Object.entries(uploadedVideosTemp).map(([uuid, upload]) => (
              <UploadQueueItem
              key={upload.videoId}
              uuid={uuid}
              progress={1}
              completed={true}
              completedText={completedText}
              redirectOnClick={redirectOnClick}
              />
            ))}
            {currentUpload && (
              <UploadQueueItem
              uuid={currentUpload.video.uuid}
              videoMetadata={currentUploadMetadata}
              progress={currentUpload.progress}
              handlePauseCurrentUpload={handlePauseCurrentUpload}
              currentEta={currentEta}
              etaText={etaText}
              completedText={completedText}
              abortUploadDialog={abortUploadDialog}
              openAbortUploadDialog={openAbortUploadDialog}
              />
            )}
            {uploadQueue.map((upload) => (
              <UploadQueueItem
              key={upload.video.uuid}
              uuid={upload.video.uuid}
              progress={upload.resumeInfo?.videoId ? startedUploads[upload.resumeInfo.videoId]?.progress : undefined}
              abortUploadDialog={abortUploadDialog}
              openAbortUploadDialog={openAbortUploadDialog}
              />
            ))}
          </div>
        )}
        {abortUploadDialog}
      </div>
    );
  }

  const UploadQueueControl = uploadQueueControlHOC ? uploadQueueControlHOC(_UploadQueueControl) : _UploadQueueControl;
  const MemoizedUploadQueueControl = React.memo(UploadQueueControl);
  return Object.assign(MemoizedUploadQueueControl, {
    UploadQueueControl: MemoizedUploadQueueControl,
    UploadQueueItem,
  });
}
